import React, { useEffect, useRef } from "react"

import { AvailableCoverageHistorySortField } from "@common-shared/config/coverageHistory.config.ts"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config.ts"
import { ICoverageHistoryListSort } from "@common-shared/types/internalApis/coverageHistory.types.ts"
import { IPolicyColumnConfiguration } from "@common-shared/types/policies.types.ts"
import { IOneVerifyCoverageCheckHistory } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { SpinnerElement } from "@front-shared/elements/spinner.element.tsx"

import { PolicyHeaderDragDirection } from "../../../config/policies.config"
import { ScreenScrollableTableElement } from "../../../elements/screenScrollableTable.element"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { policiesSelector } from "../../../store/selectors/policies.selector"
import { policiesSetHeaderDragDropDisabled } from "../../../store/slices/policies.slice"
import { PoliciesListRowComponent } from "./policiesListRow.component"
import { PoliciesListTableHeaderComponent } from "./policiesListTableHeader.component"

export const PoliciesListTableComponent: React.FunctionComponent<
    IPoliciesListTableComponentProps
> = props => {
    const policiesState = useAppSelector(policiesSelector)

    // Ref so it can be used in event listeners
    const previousHeaderDragDropDisabled = useRef(
        policiesState.policiesHeaderDragDropDisabled,
    )

    const dispatch = useAppDispatch()

    const firstTableRowRef = useRef<HTMLDivElement>(null)

    // Monitor first row to become hidden in viewport and disable drag and drop in header
    // Otherwise there's conflict between sticky and dnd
    useEffect(() => {
        if (!firstTableRowRef.current) {
            return
        }

        const onWindowScroll = () => {
            if (!firstTableRowRef.current) {
                return
            }

            const rect = firstTableRowRef.current.getBoundingClientRect()

            // rect.bottom needs to be more than 0
            const isFirstRowVisible = (rect?.bottom || 0) > 0
            const policiesHeaderDragDropDisabled = !isFirstRowVisible

            // Only update state when changes
            if (
                previousHeaderDragDropDisabled.current !==
                policiesHeaderDragDropDisabled
            ) {
                // Record for future checks
                previousHeaderDragDropDisabled.current =
                    policiesHeaderDragDropDisabled

                dispatch(
                    policiesSetHeaderDragDropDisabled(
                        policiesHeaderDragDropDisabled,
                    ),
                )
            }
        }

        window.addEventListener("scroll", onWindowScroll)

        return () => {
            window.removeEventListener("scroll", onWindowScroll)
        }
    }, [firstTableRowRef.current])

    const onSelectAllItems = () => {
        props.onSelectedItems?.(
            props.policies.filter(
                item => !!props.isSinglePolicySelectable?.(item),
            ),
        )
    }

    const onSinglePolicySelected = (policy: IOneVerifyCoverageCheckHistory) => {
        props.onSelectedItems?.([policy])
    }

    const onSinglePolicyDeselected = (
        policy: IOneVerifyCoverageCheckHistory,
    ) => {
        props.onDeselectedItems?.([policy])
    }

    return (
        <div className={`relative ${props.className}`}>
            <ScreenScrollableTableElement
                tableRef={props.scrollableElementRef}
                stickyHeaderVerticalScrollElementSelector={
                    props.stickyHeaderVerticalScrollElementSelector
                }
                stickyHeaderHorizontalScrollElement={
                    props.stickyHeaderHorizontalScrollElement
                }
                stickyHeadersDisabled={
                    !props.forceHeaderStickiness &&
                    !policiesState.policiesHeaderDragDropDisabled
                }
                headers={
                    <PoliciesListTableHeaderComponent
                        listType={props.listType}
                        policies={props.policies}
                        columns={props.columns}
                        policiesSelectable={props.policiesSelectable}
                        sortEnabled={props.sortEnabled}
                        sortConfiguration={props.sortConfiguration}
                        selectedItems={props.selectedItems}
                        onSetSortField={props.onSetSortField}
                        onSelectAllItems={onSelectAllItems}
                        onDeselectAllItems={props.onDeselectedAllItems}
                        onChangeColumnOrder={props.onChangeColumnOrder}
                        ignoreHorizontalPadding={props.ignoreHorizontalPadding}
                        isSinglePolicySelectable={
                            props.isSinglePolicySelectable
                        }
                    />
                }
            >
                {props.isListLoading ? (
                    <>
                        <div className="fixed w-screen text-center mt-[32px]">
                            <SpinnerElement />
                        </div>

                        {/*space holder since above is fixed to move with scroll*/}
                        <div className="mb-[96px]" />
                    </>
                ) : null}

                {props.policies.map((item, index) => {
                    const isSelected = !!props.selectedItems?.find(
                        innerItem =>
                            innerItem.coverageResult.resultId ===
                            item.coverageResult.resultId,
                    )

                    const ignoreRowBorder =
                        props.ignoreLastItemBorder &&
                        index === props.policies.length - 1

                    return (
                        <PoliciesListRowComponent
                            key={index}
                            elementRef={!index ? firstTableRowRef : undefined}
                            listType={props.listType}
                            columns={props.columns}
                            policy={item}
                            highlightOnHover={props.policies.length > 1}
                            ignoreNavigateToPolicy={
                                props.ignoreNavigateToPolicy
                            }
                            ignoreRowBorder={ignoreRowBorder}
                            isSelectable={props.policiesSelectable}
                            isSelectionCheckboxDisabled={
                                !props.isSinglePolicySelectable?.(item)
                            }
                            isExternallySelected={isSelected}
                            onSelected={onSinglePolicySelected}
                            onDeselectedItem={onSinglePolicyDeselected}
                            ignoreHorizontalPadding={
                                props.ignoreHorizontalPadding
                            }
                            withRowActionsOverlay
                        />
                    )
                })}
            </ScreenScrollableTableElement>
        </div>
    )
}

interface IPoliciesListTableComponentProps {
    className?: string
    scrollableElementRef?: React.RefObject<HTMLDivElement>
    policies: IOneVerifyCoverageCheckHistory[]
    columns: IPolicyColumnConfiguration[]
    onChangeColumnOrder?: (
        columnLabelMoveSource: string,
        columnLabelMoveTarget: string,
        direction: PolicyHeaderDragDirection,
    ) => void
    sortConfiguration?: ICoverageHistoryListSort
    onSetSortField?: (newSortField: AvailableCoverageHistorySortField) => void
    listType: PoliciesListType
    sortEnabled?: boolean
    policiesSelectable?: boolean
    ignoreNavigateToPolicy?: boolean
    isListLoading?: boolean
    selectedItems?: IOneVerifyCoverageCheckHistory[]
    onSelectedItems?: (items: IOneVerifyCoverageCheckHistory[]) => void
    onDeselectedItems?: (items: IOneVerifyCoverageCheckHistory[]) => void
    onDeselectedAllItems?: () => void
    ignoreLastItemBorder?: boolean
    ignoreHorizontalPadding?: boolean
    forceHeaderStickiness?: boolean
    stickyHeaderHorizontalScrollElement?: React.RefObject<HTMLDivElement>
    stickyHeaderVerticalScrollElementSelector?: string // class selector of some parent
    isSinglePolicySelectable?: (item: IOneVerifyCoverageCheckHistory) => boolean
}
